import { SEVERITY_PALETTE, type SeverityKey, type SeverityRoles } from "@safers/ui";
import type { SiteSeverity } from "@safers/api";
import { disconnectedBadgeSvg } from "@safers/model/pin";

// SEVERITY_PALETTE 의 얇은 파생 — 기존 이름을 유지하려는 것뿐이다. 새 코드는 팔레트를 직접 쓴다.

// 캐스트 대상이 SeverityKey 인 게 중요하다. SEVERITY_PALETTE 가 Record<SeverityKey, …> 라
// 이 단언은 구성상 참이다. 반면 SiteSeverity 로 캐스트하면 두 집합이 갈라져도 조용히
// 통과해, 색이 없는 등급이 런타임에 fill="undefined" 로 나간다. 다리는 아래 export 의
// 타입 표기가 검사한다 — SiteSeverity 에만 있는 등급이 생기면 거기서 컴파일이 깨진다.
const byRole = <K extends keyof SeverityRoles>(role: K) =>
  Object.fromEntries(Object.entries(SEVERITY_PALETTE).map(([k, v]) => [k, v[role]])) as Record<
    SeverityKey,
    string
  >;

/** 500. 글자를 얹지 말 것 — 대비 미달 */
export const SEVERITY_COLORS: Record<SiteSeverity, string> = byRole("fill");

/** 700 */
export const SEVERITY_TEXT_COLORS: Record<SiteSeverity, string> = byRole("panelFg");

/** 300 */
export const SEVERITY_MAP_COLORS: Record<SiteSeverity, string> = byRole("mutedFill");

export const SEVERITY_LABELS: Record<SiteSeverity, string> = {
  NORMAL: "정상",
  ATTENTION: "관심",
  CAUTION: "주의",
  WARNING: "경고",
  CRITICAL: "위험",
  UNCERTAIN: "불확실",
};

/** 대시보드 스트립·현황판의 **표시 순서**. 전 등급 목록이 아니다 — UNCERTAIN 은 제외한다. */
export const SEVERITY_ORDER: SiteSeverity[] = [
  "NORMAL",
  "ATTENTION",
  "CAUTION",
  "WARNING",
  "CRITICAL",
];

const RANK: Record<SiteSeverity, number> = {
  NORMAL: 0,
  UNCERTAIN: 1,
  ATTENTION: 2,
  CAUTION: 3,
  WARNING: 4,
  CRITICAL: 5,
};

export function shouldPulse(severity: SiteSeverity): boolean {
  return RANK[severity] >= RANK.CAUTION;
}

export function worstSeverity(severities: SiteSeverity[]): SiteSeverity {
  return severities.reduce<SiteSeverity>(
    (worst, cur) => (RANK[cur] > RANK[worst] ? cur : worst),
    "NORMAL"
  );
}

export function severityColor(severity: SiteSeverity | null | undefined): string {
  return SEVERITY_COLORS[severity ?? "NORMAL"] ?? SEVERITY_COLORS.NORMAL;
}

export function severityTextColor(severity: SiteSeverity | null | undefined): string {
  return SEVERITY_TEXT_COLORS[severity ?? "NORMAL"] ?? SEVERITY_TEXT_COLORS.NORMAL;
}

export const PIN = {
  path: "M14.5 1C20.8513 1 26 6.22371 26 12.667C25.9999 15.9322 24.676 18.8824 22.5449 21H22.5479L14.5 31L6.50391 21.0469C4.34468 18.9261 3.00011 15.9567 3 12.667C3 6.22374 8.14872 1.00005 14.5 1Z",
  width: 30,
  height: 31,
  anchorX: 14.5,
  anchorY: 12.5,
  innerRadius: 6.5,
} as const;

export const PIN_ICON_SIZE: [number, number] = [PIN.width, PIN.height];
export const PIN_ICON_ANCHOR: [number, number] = [PIN.width / 2, PIN.height];

export function severityMapColor(s: SiteSeverity): string {
  return SEVERITY_MAP_COLORS[s] ?? SEVERITY_MAP_COLORS.NORMAL;
}

export function createPoiHtml(opts: {
  severity: SiteSeverity;
  label?: string;
  selected?: boolean;
  scale?: number;
  spawnStyle?: string;
}): string {
  // 클러스터와 같은 색을 쓴다. 핀만 따로 두면 현장 하나일 때와 둘이 묶였을 때
  // 같은 등급이 다른 색으로 보인다.
  const color = severityColor(opts.severity);
  const scale = opts.scale ?? 1;
  const w = Math.round(PIN.width * scale);
  const h = Math.round(PIN.height * scale);
  const pulse = shouldPulse(opts.severity)
    ? `<span class="safers-pin__pulse"></span><span class="safers-pin__pulse safers-pin__pulse--delayed"></span>`
    : "";
  const label = opts.label
    ? `<span class="safers-pin__label">${escapeHtml(opts.label)}</span>`
    : "";

  return `<span class="safers-pin${opts.selected ? " is-selected" : ""}${opts.spawnStyle ? " is-spawning" : ""}" style="--poi-color:${color};${opts.spawnStyle ?? ""}">
    ${pulse}
    <svg class="safers-pin__svg" width="${w}" height="${h}" viewBox="0 0 ${PIN.width} ${PIN.height}" aria-hidden="true">
      <path d="${PIN.path}" fill="${color}"/>
      <circle cx="${PIN.anchorX}" cy="${PIN.anchorY}" r="${PIN.innerRadius}" fill="#fff"/>
    </svg>
    ${label}
  </span>`;
}

export function pinIconBox(scale: number): {
  size: [number, number];
  anchor: [number, number];
} {
  const w = Math.round(PIN.width * scale);
  const h = Math.round(PIN.height * scale);
  return { size: [w, h], anchor: [w / 2, h] };
}

export function createDotHtml(opts: { severity: SiteSeverity; selected?: boolean }): string {
  const color = severityMapColor(opts.severity);
  const pulse = shouldPulse(opts.severity) ? `<span class="safers-dot__pulse"></span>` : "";
  return `<span class="safers-dot${opts.selected ? " is-selected" : ""}" style="--poi-color:${color}">${pulse}<span class="safers-dot__core"></span></span>`;
}

export const DOT_ICON_SIZE: [number, number] = [14, 14];
export const DOT_ICON_ANCHOR: [number, number] = [7, 7];

export function createClusterHtml(opts: {
  count: number;
  severity: SiteSeverity;
  disconnected?: boolean;
  pulse?: boolean;
}): string {
  const color = severityColor(opts.severity);
  const pulse =
    (opts.pulse ?? shouldPulse(opts.severity)) ? `<span class="safers-cluster__pulse"></span>` : "";
  const disconnected = opts.disconnected
    ? `<svg class="safers-cluster__disconnected" viewBox="0 0 16 16" aria-hidden="true">${disconnectedBadgeSvg(8, 8, 7)}</svg>`
    : "";
  // 숫자를 `__body` 안에 넣지 말 것 — hover 확대(transform)가 글자까지 끌고 가
  // 글자 크기와 안티에일리어싱이 상태마다 달라진다. 핀이 `__label` 을 `__svg` 의
  // 형제로 두는 것과 같은 이유다.
  return `<span class="safers-cluster" style="--poi-color:${color}">
    ${pulse}
    <span class="safers-cluster__ring"></span>
    <span class="safers-cluster__body"></span>
    <span class="safers-cluster__count">${opts.count}</span>
    ${disconnected}
  </span>`;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!
  );
}
